import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { registerSchema, type RegisterDto } from '@fp3/shared'
import { useRegister } from '../hooks/useRegister'
import { getErrorMessage, isConflictError, isErrorStatus } from '@/lib/utils/errors'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'

export const RegisterForm = () => {
  const {
    register,
    handleSubmit,
    formState: { errors, isDirty, isValid },
  } = useForm<RegisterDto>({
    resolver: zodResolver(registerSchema),
    mode: 'onChange',
  })

  const { mutate: registerUser, isPending, error } = useRegister()

  const onSubmit = (data: RegisterDto) => {
    registerUser(data)
  }

  const hasValidationErrors = Object.keys(errors).length > 0
  const isEmailExistsError = isConflictError(error)
  const isRegistrationDisabled = isErrorStatus(error, 403)

  if (isRegistrationDisabled) {
    return (
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>Registration Temporarily Unavailable</CardTitle>
          <CardDescription>
            New account registration is currently closed. If you already have an account, please log
            in.
          </CardDescription>
        </CardHeader>
      </Card>
    )
  }

  return (
    <Card className="w-full max-w-md">
      <CardHeader>
        <CardTitle>Create Account</CardTitle>
        <CardDescription>Enter your information to create a new account</CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {error && !hasValidationErrors && (
            <div
              className="p-3 text-sm text-destructive bg-destructive/10 border border-destructive/20 rounded-md"
              role="alert"
              data-testid="auth-register-error-alert"
            >
              <p className="font-medium mb-1">Registration failed</p>
              <p>{getErrorMessage(error)}</p>
              {isEmailExistsError && (
                <p className="text-xs mt-2 opacity-90">
                  The email you entered is already registered. Please log in instead.
                </p>
              )}
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="name">Name</Label>
            <Input
              id="name"
              type="text"
              placeholder="John Doe"
              aria-invalid={!!errors.name}
              aria-describedby={errors.name ? 'name-error' : undefined}
              data-testid="auth-register-name-input"
              {...register('name')}
            />
            {errors.name && (
              <p
                id="name-error"
                className="text-sm text-destructive"
                data-testid="auth-register-name-error"
              >
                {errors.name.message}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              placeholder="you@example.com"
              aria-invalid={!!errors.email}
              aria-describedby={errors.email ? 'email-error' : undefined}
              data-testid="auth-register-email-input"
              {...register('email')}
            />
            {errors.email && (
              <p
                id="email-error"
                className="text-sm text-destructive"
                data-testid="auth-register-email-error"
              >
                {errors.email.message}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="password">Password</Label>
            <Input
              id="password"
              type="password"
              placeholder="••••••••"
              aria-invalid={!!errors.password}
              aria-describedby={errors.password ? 'password-error' : undefined}
              data-testid="auth-register-password-input"
              {...register('password')}
            />
            {errors.password && (
              <p
                id="password-error"
                className="text-sm text-destructive"
                data-testid="auth-register-password-error"
              >
                {errors.password.message}
              </p>
            )}
            <p className="text-xs text-muted-foreground mt-1">
              Password must be at least 8 characters with a number and special character (!@#$%^&*)
            </p>
          </div>

          <Button
            type="submit"
            className="w-full"
            disabled={isPending || !isDirty || !isValid}
            data-testid="auth-register-submit"
          >
            {isPending ? 'Creating account...' : 'Create Account'}
          </Button>
        </form>
      </CardContent>
    </Card>
  )
}
